/**
 * @fileoverview Modal de transferência de ticket (seleção de atendente + fila).
 * Paleta alinhada ao modal Gerenciar Etiquetas.
 */

import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";

import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import QueuePickerPopover from "../QueuePickerPopover";
import UserPickerPopover from "../UserPickerPopover";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useQueues } from "../../context/Queues/QueuesContext";
import { AuthContext } from "../../context/Auth/AuthContext";

import { X } from "lucide-react";
const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    dialog: {
      "& .MuiDialog-paper": {
        fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
        width: "min(460px, calc(100vw - 32px))",
        maxWidth: "calc(100vw - 32px)",
        borderRadius: 24,
        overflow: "hidden",
        background: dark
          ? "linear-gradient(180deg, #171a20 0%, #111319 100%)"
          : "#ffffff",
        boxShadow: dark
          ? "0 24px 80px rgba(0, 0, 0, 0.45)"
          : "0 24px 60px rgba(15, 23, 42, 0.12)",
        border: dark
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(15, 23, 42, 0.08)",
        "& *": {
          fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(2.5, 3),
      borderBottom: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
    },
    title: {
      fontSize: "1.35rem",
      fontWeight: 700,
      color: dark ? "#f8fafc" : "#111827",
      lineHeight: 1.2,
    },
    close: {
      color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
    },
    content: {
      padding: theme.spacing(3),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2.5),
    },
    fieldLabel: {
      fontSize: 10,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.14em",
      color: dark ? "rgba(248, 250, 252, 0.5)" : "#9ca3af",
      marginBottom: 8,
    },
    actions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 10,
      padding: theme.spacing(2, 3, 2.5),
      borderTop: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
    },
    btnGhost: {
      height: 36,
      minWidth: 110,
      padding: "0 16px",
      borderRadius: 10,
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
      background: "transparent",
      color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
      fontSize: "0.95rem",
      fontWeight: 500,
      cursor: "pointer",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      fontFamily: "inherit",
      textTransform: "none",
      "&:hover:not(:disabled)": {
        background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
        color: dark ? "#f8fafc" : "#111827",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    btnPrimary: {
      height: 36,
      minWidth: 120,
      padding: "0 18px",
      borderRadius: 10,
      border: "none",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      fontSize: "0.95rem",
      fontWeight: 500,
      cursor: "pointer",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      fontFamily: "inherit",
      textTransform: "none",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      boxShadow: "none",
      "&:hover:not(:disabled)": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      },
      "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
    },
    spin: { animation: "$spin 0.8s linear infinite" },
    "@keyframes spin": {
      "0%": { transform: "rotate(0deg)" },
      "100%": { transform: "rotate(360deg)" },
    },
  };
});

const Spinner = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const TransferTicketModalCustom = ({ modalOpen, onClose, ticketid }) => {
  const history = useHistory();
  const classes = useStyles();
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(undefined);
  const { queues: contextQueues } = useQueues();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (user.queues.length === 1) {
      setSelectedQueue(user.queues[0].id);
    }
  }, [user.queues]);

  useEffect(() => {
    if (isMounted.current && contextQueues.length > 0) {
      setAllQueues(contextQueues);
      setQueues(contextQueues);
    }
  }, [contextQueues]);

  const handleClose = () => {
    if (onClose) onClose();
    setSelectedUser(null);
  };

  const handleSelectUser = (newUser) => {
    setSelectedUser(newUser);
    if (newUser && Array.isArray(newUser.queues)) {
      setQueues(newUser.queues);
    } else {
      setQueues(allQueues);
      setSelectedQueue(undefined);
    }
  };

  const handleSaveTicket = async (e) => {
    e.preventDefault();
    if (!ticketid) return;

    const hasQueue = selectedQueue !== "" && selectedQueue !== null && selectedQueue !== undefined;

    if (!selectedUser && !hasQueue) {
      toast.error(i18n.t("transferTicketModal.noSelection"));
      return;
    }

    setLoading(true);
    try {
      const data = {};
      if (selectedUser) data.userId = selectedUser.id;
      if (hasQueue) {
        data.queueId = selectedQueue;
        if (!selectedUser) {
          data.status = "pending";
          data.userId = null;
        }
      } else if (selectedUser) {
        data.queueId = null;
      }

      await api.put(`/tickets/${ticketid}`, data);

      setLoading(false);
      handleClose();
      history.push(`/tickets`);
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  return (
    <Dialog
      open={!!modalOpen}
      onClose={handleClose}
      className={classes.dialog}
      maxWidth={false}
      scroll="paper"
    >
      <form onSubmit={handleSaveTicket}>
        <div className={classes.header}>
          <Typography className={classes.title}>
            {i18n.t("transferTicketModal.title")}
          </Typography>
          <IconButton size="small" onClick={handleClose} className={classes.close}>
            <X size={18} />
          </IconButton>
        </div>

        <div className={classes.content}>
          <div>
            <Typography className={classes.fieldLabel}>
              {i18n.t("transferTicketModal.fieldUserLabel")}
            </Typography>
            <UserPickerPopover
              value={selectedUser}
              onChange={handleSelectUser}
              placeholder={i18n.t("transferTicketModal.userPlaceholder")}
              title={i18n.t("transferTicketModal.userPopoverTitle")}
              searchPlaceholder={i18n.t("transferTicketModal.userSearchPlaceholder")}
              clearLabel={i18n.t("transferTicketModal.clearUserOption")}
              noUsersFoundLabel={i18n.t("transferTicketModal.noUsersFound")}
              singleSelectedLabel={i18n.t("transferTicketModal.singleSelected")}
              showingAllLabel={i18n.t("transferTicketModal.showingAllUsers")}
              fullWidth
            />
          </div>
          <div>
            <Typography className={classes.fieldLabel}>
              {i18n.t("transferTicketModal.fieldQueueLabel")}
            </Typography>
            <QueuePickerPopover
              value={selectedQueue}
              onChange={setSelectedQueue}
              queues={queues}
              placeholder={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
              title={i18n.t("transferTicketModal.queuePopoverTitle")}
              searchPlaceholder={i18n.t("transferTicketModal.queueSearchPlaceholder")}
              noQueueLabel={i18n.t("transferTicketModal.noQueueOption")}
              noQueuesFoundLabel={i18n.t("transferTicketModal.noQueuesFound")}
              singleSelectedLabel={i18n.t("transferTicketModal.singleSelected")}
              showingAllLabel={i18n.t("transferTicketModal.showingAll")}
              fullWidth
            />
          </div>
        </div>

        <div className={classes.actions}>
          <button
            type="button"
            className={classes.btnGhost}
            onClick={handleClose}
            disabled={loading}
          >
            {i18n.t("transferTicketModal.buttons.cancel")}
          </button>
          <button type="submit" className={classes.btnPrimary} disabled={loading}>
            {loading && <Spinner className={classes.spin} />}
            {i18n.t("transferTicketModal.buttons.ok")}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default TransferTicketModalCustom;
